import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Sheet } from '@/components/ui/Sheet'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { archivePlan, createPlan, endEnrollment, enrolMember, listEnrollments, listPlans, monthsToBill } from '@/data/dues'
import type { Cadence, DuesPlan, Enrollment } from '@/data/dues'
import { listDepartmentMembers } from '@/data/members'
import { formatKwacha } from '@/lib/money'
import { lusakaToday } from '@/lib/periods'
import { amountToNgwee, enrolFormSchema, planFormSchema } from './schemas'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const monthName = (m: number) => MONTHS[m - 1] ?? ''
/** 'YYYY-MM...' -> 'July 2026' (no Date parsing, so no timezone shifts). */
function monthYear(date: string): string {
  const [y, m] = date.split('-')
  return `${monthName(Number(m))} ${y}`
}
function ordinal(n: number): string {
  const v = n % 100
  if (v >= 11 && v <= 13) return `${n}th`
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`
}
function joinWords(xs: string[]): string {
  if (xs.length <= 1) return xs.join('')
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`
}
/** Month id ('YYYY-MM') going back `back` months from `ym`. */
function shiftMonth(ym: string, back: number): string {
  const [y, m] = ym.split('-').map(Number)
  const idx = y * 12 + (m - 1) - back
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`
}
const cadenceText = (c: Cadence) => (c === 'monthly' ? 'every month' : 'every 3 months')
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

function Loading() {
  return (
    <SkeletonRows rows={4} />
  )
}

function LoadError({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div>
      <p role="alert" className="text-body text-bad">{errText(error)}</p>
      <Button variant="secondary" size="lg" className="mt-3" onClick={retry}>Try again</Button>
    </div>
  )
}

function ActionButton({ children, onClick }: { children: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-[44px] shrink-0 items-center rounded-control px-3 text-body font-medium text-bad hover:bg-neutral-tint"
    >
      {children}
    </button>
  )
}

export function DuesPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const qc = useQueryClient()
  const today = lusakaToday()
  const thisMonth = today.slice(0, 7)

  const plansQ = useQuery({ queryKey: ['plans', deptId], queryFn: () => listPlans(deptId) })
  const enrolQ = useQuery({ queryKey: ['enrollments', deptId], queryFn: () => listEnrollments(deptId) })
  const membersQ = useQuery({ queryKey: ['members', deptId], queryFn: () => listDepartmentMembers(deptId) })

  const toast = useToast()
  const [planSheet, setPlanSheet] = useState(false)
  const [enrolSheet, setEnrolSheet] = useState(false)
  const [removePlan, setRemovePlan] = useState<DuesPlan | null>(null)
  const [stopRow, setStopRow] = useState<Enrollment | null>(null)

  const [pName, setPName] = useState('')
  const [pCadence, setPCadence] = useState<Cadence>('monthly')
  const [pAmount, setPAmount] = useState('')
  const [pDay, setPDay] = useState('7')
  const [pStart, setPStart] = useState(`${thisMonth}-01`)
  const [pErrors, setPErrors] = useState<Record<string, string>>({})
  const [pFormError, setPFormError] = useState('')
  const planBusy = useRef(false)

  const [eMember, setEMember] = useState('')
  const [ePlan, setEPlan] = useState('')
  const [eMonth, setEMonth] = useState(thisMonth)
  const [eOverride, setEOverride] = useState('')
  const [eErrors, setEErrors] = useState<Record<string, string>>({})
  const [eFormError, setEFormError] = useState('')
  const enrolBusy = useRef(false)

  const [actionError, setActionError] = useState('')

  const invalidateEnrol = () =>
    Promise.all(['enrollments', 'members', 'arrears'].map((k) => qc.invalidateQueries({ queryKey: [k, deptId] })))

  const createPlanM = useMutation({ mutationFn: createPlan })
  const archiveM = useMutation({ mutationFn: archivePlan })
  const enrolM = useMutation({ mutationFn: (v: Parameters<typeof enrolMember>[0]) => enrolMember(v, lusakaToday()) })
  const endM = useMutation({ mutationFn: (id: string) => endEnrollment(id, lusakaToday()) })

  const plans = plansQ.data ?? []
  const enrollments = enrolQ.data ?? []
  const members = membersQ.data ?? []
  const nameOf = new Map(members.map((m) => [m.memberId, m.fullName]))
  const enrolledIds = new Set(enrollments.map((e) => e.memberId))
  const available = members.filter((m) => m.leftOn === null && !enrolledIds.has(m.memberId))
  const notPaying = available.length

  const openPlanSheet = () => {
    setPName(''); setPCadence('monthly'); setPAmount(''); setPDay('7'); setPStart(`${thisMonth}-01`)
    setPErrors({}); setPFormError(''); setPlanSheet(true)
  }

  const submitPlan = async (e: FormEvent) => {
    e.preventDefault()
    if (planBusy.current) return
    setPFormError('')
    const parsed = planFormSchema.safeParse({ name: pName, cadence: pCadence, amount: pAmount, dueDay: pDay, startsOn: pStart })
    if (!parsed.success) {
      const errs: Record<string, string> = {}
      for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
      setPErrors(errs)
      return
    }
    setPErrors({})
    const v = parsed.data
    planBusy.current = true
    try {
      await createPlanM.mutateAsync({
        departmentId: deptId,
        name: v.name,
        cadence: v.cadence,
        amountNgwee: amountToNgwee(v.amount) as number,
        dueDay: Number(v.dueDay),
        effectiveFrom: v.startsOn,
      })
      await qc.invalidateQueries({ queryKey: ['plans', deptId] })
      setPlanSheet(false)
      toast.success(`The plan "${v.name}" was added.`)
    } catch (err) {
      setPFormError(errText(err))
    } finally {
      planBusy.current = false
    }
  }

  const openEnrolSheet = () => {
    setEMember(''); setEPlan(plans.length === 1 ? plans[0].id : ''); setEMonth(thisMonth); setEOverride('')
    setEErrors({}); setEFormError(''); setEnrolSheet(true)
  }

  const submitEnrol = async (e: FormEvent) => {
    e.preventDefault()
    if (enrolBusy.current) return
    setEFormError('')
    const parsed = enrolFormSchema.safeParse({ membershipId: eMember, planId: ePlan, month: eMonth, override: eOverride })
    const errs: Record<string, string> = {}
    if (!parsed.success) for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
    if (/^\d{4}-\d{2}$/.test(eMonth)) {
      if (eMonth > thisMonth) errs.month = 'Pick this month or an earlier month'
      else if (eMonth < shiftMonth(thisMonth, 12)) errs.month = 'Pick a month within the last 12 months'
    }
    setEErrors(errs)
    if (!parsed.success || Object.keys(errs).length > 0) return
    const v = parsed.data
    const member = available.find((m) => m.membershipId === v.membershipId)
    if (!member) {
      setEErrors({ membershipId: 'Pick a member' })
      return
    }
    enrolBusy.current = true
    try {
      await enrolM.mutateAsync({
        departmentId: deptId,
        membershipId: member.membershipId,
        memberId: member.memberId,
        planId: v.planId,
        startOn: `${v.month}-01`,
        overrideAmountNgwee: v.override.trim() === '' ? null : amountToNgwee(v.override),
      })
      await invalidateEnrol()
      setEnrolSheet(false)
      toast.success(`${member.fullName} was added to dues.`)
    } catch (err) {
      setEFormError(errText(err))
      void invalidateEnrol()
    } finally {
      enrolBusy.current = false
    }
  }

  const confirmRemovePlan = async () => {
    if (!removePlan || archiveM.isPending) return
    setActionError('')
    try {
      await archiveM.mutateAsync(removePlan.id)
      await qc.invalidateQueries({ queryKey: ['plans', deptId] })
      toast.success(`The plan "${removePlan.name}" was removed.`)
      setRemovePlan(null)
    } catch (err) {
      setActionError(errText(err))
    }
  }

  const confirmStop = async () => {
    if (!stopRow || endM.isPending) return
    setActionError('')
    try {
      await endM.mutateAsync(stopRow.id)
      await invalidateEnrol()
      toast.success(`${nameOf.get(stopRow.memberId) ?? 'That person'} will no longer be billed.`)
      setStopRow(null)
    } catch (err) {
      setActionError(errText(err))
    }
  }

  const selectedPlan = plans.find((p) => p.id === ePlan)
  // A quarterly plan only bills in January, April, July and October.
  const billMonths = (/^\d{4}-\d{2}$/.test(eMonth) && eMonth <= thisMonth ? monthsToBill(`${eMonth}-01`, today) : []).filter(
    (d) => selectedPlan?.cadence !== 'quarterly' || [1, 4, 7, 10].includes(Number(d.slice(5, 7))),
  )
  const billNames = billMonths.map((d) => monthName(Number(d.slice(5, 7))))
  const sinceText =
    billMonths.length > 1
      ? `Choosing ${billNames[0]} will bill them for ${joinWords(billNames)}. Pick this month to bill from now on only.`
      : billMonths.length === 1
        ? `They will be billed for ${billNames[0]}.`
        : ''


  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-title font-semibold text-ink">Dues plans</h2>


      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">Plans</h3>
          {canWrite && <Button size="lg" onClick={openPlanSheet}>Add a plan</Button>}
        </CardHeader>
        <CardBody>
          {plansQ.isPending ? (
            <Loading />
          ) : plansQ.isError ? (
            <LoadError error={plansQ.error} retry={() => void plansQ.refetch()} />
          ) : plans.length === 0 ? (
            <EmptyState title="No plans yet" description="A plan says how much people pay and how often." />
          ) : (
            <ul className="divide-y divide-line">
              {plans.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{p.name}</p>
                    <p className="text-body text-muted">
                      {formatKwacha(p.amountNgwee)} {cadenceText(p.cadence)}, due on the {ordinal(p.dueDay)}
                    </p>
                  </div>
                  {canWrite && (
                    <ActionButton onClick={() => { setActionError(''); setRemovePlan(p) }}>Remove</ActionButton>
                  )}
                  <OwnerDeleteButton table="dues_plan" id={p.id} what="dues plan" className="ml-2" />
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">Who pays dues</h3>
          {canWrite && <Button size="lg" onClick={openEnrolSheet}>Add someone to dues</Button>}
        </CardHeader>
        <CardBody>
          {enrolQ.isPending || membersQ.isPending ? (
            <Loading />
          ) : enrolQ.isError || membersQ.isError ? (
            <LoadError
              error={enrolQ.error ?? membersQ.error}
              retry={() => { void enrolQ.refetch(); void membersQ.refetch() }}
            />
          ) : (
            <>
              {notPaying > 0 && (
                <p className="mb-3 rounded-control bg-wait-tint px-4 py-3 text-body text-wait">
                  {notPaying === 1 ? '1 member is not paying dues yet' : `${notPaying} members are not paying dues yet`}
                </p>
              )}
              {enrollments.length === 0 ? (
                <EmptyState title="Nobody is paying dues yet" description="Add a member to a plan to start billing them." />
              ) : (
                <ul className="divide-y divide-line">
                  {[...enrollments]
                    .sort((a, b) => (nameOf.get(a.memberId) ?? '').localeCompare(nameOf.get(b.memberId) ?? ''))
                    .map((en) => (
                      <li key={en.id} className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0">
                          <p className="font-medium text-ink">{nameOf.get(en.memberId) ?? 'Unnamed member'}</p>
                          <p className="text-body text-muted">
                            {en.planName}, {formatKwacha(en.amountNgwee)} {cadenceText(en.cadence)}
                          </p>
                          <p className="text-micro text-muted">since {monthYear(en.startOn)}</p>
                        </div>
                        {canWrite && (
                          <ActionButton onClick={() => { setActionError(''); setStopRow(en) }}>Stop billing</ActionButton>
                        )}
                      </li>
                    ))}
                </ul>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={planSheet} onClose={() => setPlanSheet(false)} title="Add a plan">
        <form onSubmit={(e) => void submitPlan(e)} className="flex flex-col gap-4" noValidate>
          <Input label="Name" placeholder="Monthly dues" value={pName} onChange={(e) => setPName(e.target.value)} error={pErrors.name} />
          <Select
            label="How often"
            value={pCadence}
            onChange={(e) => setPCadence(e.target.value as Cadence)}
            options={[{ value: 'monthly', label: 'Every month' }, { value: 'quarterly', label: 'Every 3 months' }]}
          />
          <Input label="Amount in Kwacha" inputMode="decimal" placeholder="50.00" value={pAmount} onChange={(e) => setPAmount(e.target.value)} error={pErrors.amount} />
          <Input
            label="Due day of the month"
            type="number"
            inputMode="numeric"
            min={1}
            max={28}
            value={pDay}
            onChange={(e) => setPDay(e.target.value)}
            hint="Pick a day from 1 to 28."
            error={pErrors.dueDay}
          />
          <Input label="Starts from" type="date" value={pStart} onChange={(e) => setPStart(e.target.value)} error={pErrors.startsOn} />
          {pFormError && <p role="alert" className="text-body text-bad">{pFormError}</p>}
          <Button type="submit" size="lg" isLoading={createPlanM.isPending}>
            {createPlanM.isPending ? 'Saving...' : 'Save plan'}
          </Button>
        </form>
      </Sheet>

      <Sheet isOpen={enrolSheet} onClose={() => setEnrolSheet(false)} title="Add someone to dues">
        <form onSubmit={(e) => void submitEnrol(e)} className="flex flex-col gap-4" noValidate>
          {available.length === 0 ? (
            <p className="text-neutral">Everyone is already on a plan.</p>
          ) : plans.length === 0 ? (
            <p className="text-neutral">Create a plan first.</p>
          ) : (
            <>
              <Select
                label="Member"
                value={eMember}
                onChange={(e) => setEMember(e.target.value)}
                options={[{ value: '', label: 'Choose a member' }, ...available.map((m) => ({ value: m.membershipId, label: m.fullName }))]}
                error={eErrors.membershipId}
              />
              <Select
                label="Plan"
                value={ePlan}
                onChange={(e) => setEPlan(e.target.value)}
                options={[
                  { value: '', label: 'Choose a plan' },
                  ...plans.map((p) => ({ value: p.id, label: `${p.name} (${formatKwacha(p.amountNgwee)} ${cadenceText(p.cadence)})` })),
                ]}
                error={eErrors.planId}
              />
              <Input
                label="Start billing from"
                type="month"
                min={shiftMonth(thisMonth, 12)}
                max={thisMonth}
                value={eMonth}
                onChange={(e) => setEMonth(e.target.value)}
                error={eErrors.month}
              />
              {sinceText && (
                <p className="rounded-control bg-wait-tint px-4 py-3 text-body text-wait">{sinceText}</p>
              )}
              <Input
                label="Different amount for this person (optional)"
                inputMode="decimal"
                placeholder={selectedPlan ? `Blank means ${formatKwacha(selectedPlan.amountNgwee)}` : 'Blank means the plan amount'}
                value={eOverride}
                onChange={(e) => setEOverride(e.target.value)}
                error={eErrors.override}
              />
            </>
          )}
          {eFormError && <p role="alert" className="text-body text-bad">{eFormError}</p>}
          <Button type="submit" size="lg" isLoading={enrolM.isPending} disabled={available.length === 0 || plans.length === 0}>
            {enrolM.isPending ? 'Saving...' : 'Add to dues'}
          </Button>
        </form>
      </Sheet>

      <Sheet isOpen={removePlan !== null} onClose={() => setRemovePlan(null)} title="Remove this plan?">
        <div className="flex flex-col gap-4">
          <p className="text-neutral">
            {removePlan?.name}. Existing charges are kept. No new charges will be created from this plan.
          </p>
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button variant="danger" size="lg" isLoading={archiveM.isPending} onClick={() => void confirmRemovePlan()}>
            {archiveM.isPending ? 'Removing...' : 'Remove plan'}
          </Button>
          <Button variant="secondary" size="lg" onClick={() => setRemovePlan(null)}>Keep plan</Button>
        </div>
      </Sheet>

      <Sheet isOpen={stopRow !== null} onClose={() => setStopRow(null)} title="Stop billing?">
        <div className="flex flex-col gap-4">
          <p className="text-neutral">
            {stopRow ? (nameOf.get(stopRow.memberId) ?? 'This person') : ''} will not be billed for new months. Charges already made are kept.
          </p>
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button variant="danger" size="lg" isLoading={endM.isPending} onClick={() => void confirmStop()}>
            {endM.isPending ? 'Stopping...' : 'Stop billing'}
          </Button>
          <Button variant="secondary" size="lg" onClick={() => setStopRow(null)}>Keep billing</Button>
        </div>
      </Sheet>
    </div>
  )
}
